import type { RouterInputs } from '@client/trpc/client';
import { safeLocalStorage } from '@client/utils/storage/safe-storage';
import superjson from 'superjson';
import { type RecordSearchResult, type RecordSearchSection, recordSearchResultKey } from './record-search';

/**
 * The palette's recently-opened records: a small per-user, per-org ring buffer in localStorage,
 * written when a record is opened from the palette and shown when the palette opens with an empty
 * query. Purely a convenience cache — anything unreadable (corrupt payload, a shape from an older
 * revision, storage unavailable, an expired entry) degrades to "no recents", never an error.
 *
 * Only stable identifiers are stored — entity + id (invoices keep claimId + invoiceNo, their only
 * identity). The rendered content is resolved fresh through `global.globalSearch` on every palette
 * open (`useRecentRecords`), so the server's read ACLs, module gates, and Lite redaction decide
 * what still renders: access revoked after a record was opened means the entry silently drops out,
 * and names/statuses can never go stale. This replaced a v1 full-snapshot design that re-rendered
 * cached customer-facing content without asking the server.
 *
 * Serialisation is superjson, NOT bare JSON.stringify with a replacer: `client/src/main.tsx`
 * globally defines `BigInt.prototype.toJSON`, which JSON.stringify consults BEFORE any replacer,
 * so a replacer-based bigint tagging silently never runs. superjson converts bigints through its
 * own registry first and is immune (same reason the zustand persister uses it).
 */

/** Common prefix of every version's keys — the logout sweep matches on this, so v1 leftovers go too. */
const STORAGE_KEY_BASE_PREFIX = 'curium.palette.recent-records';

/** Bump when the stored shape changes incompatibly — old entries are then simply ignored. */
const STORAGE_KEY_PREFIX = `${STORAGE_KEY_BASE_PREFIX}.v2`;

/** Enough to recognise "the thing I was just working on" without crowding out the page list. */
export const MAX_RECENT_RECORDS = 5;

/**
 * Refs hold no record content, but identifiers still should not accumulate indefinitely on shared
 * machines: entries expire, and `clearRecentRecords` sweeps the prefix on logout.
 */
export const RECENT_RECORDS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/** Storage entries are namespaced per user AND org: recents must never leak across either. */
export function recentRecordsStorageKey(userId: bigint | string, orgId: bigint | string): string {
    return `${STORAGE_KEY_PREFIX}.${userId}.${orgId}`;
}

/**
 * The stored identity of a recently opened record. Deliberately content-free: everything the
 * palette renders is refetched under the caller's current access on open.
 */
export type RecentRecordRef =
    | { entity: Exclude<RecordSearchResult['entity'], 'invoice'>; id: bigint }
    | { entity: 'invoice'; claimId: bigint; invoiceNo: string };

/** Same format as `recordSearchResultKey`, so a resolved result can be matched back to its ref. */
function recentRecordRefKey(ref: RecentRecordRef): string {
    return ref.entity === 'invoice' ? `invoice-${ref.claimId}-${ref.invoiceNo}` : `${ref.entity}-${ref.id}`;
}

/** The identity ref of a result the user just opened — what `pushRecentRecord` persists. */
function toRecentRecordRef(result: RecordSearchResult): RecentRecordRef {
    return result.entity === 'invoice'
        ? { entity: 'invoice', claimId: result.claimId, invoiceNo: result.invoiceNo }
        : { entity: result.entity, id: result.id };
}

interface StoredRecentRecord {
    savedAt: number;
    ref: RecentRecordRef;
}

const ID_REF_ENTITIES = new Set<string>([
    'claim',
    'incident',
    'complaint',
    'risk',
    'control',
    'controlTest',
    'obligation',
]);
const isBigint = (value: unknown): value is bigint => typeof value === 'bigint';

/** Entries are user-writable data (localStorage) — accept only refs of exactly the stored shape. */
function isStoredRef(value: unknown): value is RecentRecordRef {
    if (value === null || typeof value !== 'object') return false;
    const ref = value as Record<string, unknown>;
    if (ref.entity === 'invoice') {
        return isBigint(ref.claimId) && typeof ref.invoiceNo === 'string';
    }
    return typeof ref.entity === 'string' && ID_REF_ENTITIES.has(ref.entity) && isBigint(ref.id);
}

function isStoredEntry(value: unknown, now: number): value is StoredRecentRecord {
    if (value === null || typeof value !== 'object') return false;
    const entry = value as Record<string, unknown>;
    return (
        typeof entry.savedAt === 'number' &&
        entry.savedAt <= now &&
        now - entry.savedAt <= RECENT_RECORDS_TTL_MS &&
        isStoredRef(entry.ref)
    );
}

type RecentRecordsStorage = Pick<Storage, 'getItem' | 'setItem'>;
type RecentRecordsCleanupStorage = Pick<Storage, 'length' | 'key' | 'removeItem'>;

function readEntries(storageKey: string, storage: RecentRecordsStorage, now: number): StoredRecentRecord[] {
    try {
        const raw = storage.getItem(storageKey);
        if (!raw) return [];
        const parsed = superjson.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((entry): entry is StoredRecentRecord => isStoredEntry(entry, now));
    } catch {
        return [];
    }
}

/** The stored, unexpired record refs, most recent first. Unreadable state degrades to empty. */
export function readRecentRecordRefs(
    storageKey: string,
    storage: RecentRecordsStorage = safeLocalStorage,
): RecentRecordRef[] {
    return readEntries(storageKey, storage, Date.now())
        .slice(0, MAX_RECENT_RECORDS)
        .map((entry) => entry.ref);
}

/**
 * Record a palette navigation: the record's ref moves to (or enters at) the front, deduplicated by
 * its entity-scoped key, capped at `MAX_RECENT_RECORDS`. Write failures (quota, private mode) are
 * swallowed — recents are never worth an error.
 */
export function pushRecentRecord(
    storageKey: string,
    result: RecordSearchResult,
    storage: RecentRecordsStorage = safeLocalStorage,
): void {
    try {
        const now = Date.now();
        const ref = toRecentRecordRef(result);
        const key = recentRecordRefKey(ref);
        const next: StoredRecentRecord[] = [
            { savedAt: now, ref },
            ...readEntries(storageKey, storage, now).filter((entry) => recentRecordRefKey(entry.ref) !== key),
        ].slice(0, MAX_RECENT_RECORDS);
        storage.setItem(storageKey, superjson.stringify(next));
    } catch {
        // Storage unavailable or full — the palette simply shows fewer recents.
    }
}

/**
 * Remove every user's recents on this browser. Called from logout: even bare identifiers should
 * not survive the session on a shared machine. Matches the unversioned base prefix, so entries
 * written by earlier storage revisions are swept too.
 */
export function clearRecentRecords(storage: RecentRecordsCleanupStorage = safeLocalStorage): void {
    try {
        const doomed: string[] = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key?.startsWith(STORAGE_KEY_BASE_PREFIX)) doomed.push(key);
        }
        for (const key of doomed) storage.removeItem(key);
    } catch {
        // Best-effort cleanup only.
    }
}

// ── Resolution (refs → fresh rows via global.globalSearch) ──────────────────────────────────────

type GlobalSearchInput = RouterInputs['global']['globalSearch'];

/**
 * The `global.globalSearch` input that resolves stored refs to fresh rows: per entity, an OR of
 * exact identity clauses. The server ANDs its read ACLs, module gates, and Lite rules onto these,
 * so whatever comes back is exactly what the caller may currently see — a ref the server does not
 * return is simply not rendered. Returns null when there is nothing to resolve.
 */
export function buildRecentRecordsInput(refs: RecentRecordRef[]): GlobalSearchInput | null {
    if (refs.length === 0) return null;

    const ids: Record<Exclude<RecentRecordRef['entity'], 'invoice'>, bigint[]> = {
        claim: [],
        incident: [],
        complaint: [],
        risk: [],
        control: [],
        controlTest: [],
        obligation: [],
    };
    const invoices: { claimId: bigint; invoiceNo: string }[] = [];
    for (const ref of refs) {
        if (ref.entity === 'invoice') {
            invoices.push(ref);
        } else {
            ids[ref.entity].push(ref.id);
        }
    }

    const idWhere = (list: bigint[]) => ({ where: { OR: list.map((id) => ({ id: { equals: id } })) } });

    return {
        limit: MAX_RECENT_RECORDS,
        offset: 0,
        preview: true,
        ...(ids.claim.length > 0 && { claim: idWhere(ids.claim) }),
        ...(ids.incident.length > 0 && { incident: idWhere(ids.incident) }),
        ...(ids.complaint.length > 0 && { complaint: idWhere(ids.complaint) }),
        ...(ids.risk.length > 0 && { risk: idWhere(ids.risk) }),
        ...(ids.control.length > 0 && { control: idWhere(ids.control) }),
        ...(ids.controlTest.length > 0 && { controlTest: idWhere(ids.controlTest) }),
        ...(ids.obligation.length > 0 && { obligation: idWhere(ids.obligation) }),
        ...(invoices.length > 0 && {
            invoice: {
                where: {
                    OR: invoices.map((invoice) => ({
                        AND: [{ claimId: { equals: invoice.claimId } }, { invoiceNo: { equals: invoice.invoiceNo } }],
                    })),
                },
            },
        }),
    };
}

/**
 * Order server-resolved rows back into the stored most-recent-first order, dropping refs the
 * server no longer returned (access revoked, module disabled, record deleted).
 */
export function orderResolvedRecents(sections: RecordSearchSection[], refs: RecentRecordRef[]): RecordSearchResult[] {
    const byKey = new Map(
        sections.flatMap((section) =>
            section.results.map((result) => [recordSearchResultKey(result), result] as const),
        ),
    );
    return refs.flatMap((ref) => {
        const result = byKey.get(recentRecordRefKey(ref));
        return result ? [result] : [];
    });
}
